import React, { Component } from 'react';
import { Link } from 'react-router-dom';
import moment from 'moment';
import PropTypes from 'prop-types';
import keyService from '../../services/keyService';
import qrService from '../../services/qrService';
import logo from '../../styles/img/Logo.png';
import { useAuth } from '../../contexts/AuthContext';
import { useTranslation } from '../../i18n';
import DeleteKeyModal from '../DeleteKeyModal';
import './ListCodes.css';

class Row extends Component {
  constructor(props) {
    super(props);
    this.state = {
      showQr: false,
      deleting: false,
      syncing: false,
      showDeleteModal: false,
    };
    this.keyCanvas = null;
    this.onDeleteClicked = this.onDeleteClicked.bind(this);
    this.onConfirmDelete = this.onConfirmDelete.bind(this);
    this.onCancelDelete = this.onCancelDelete.bind(this);
    this.onSyncClicked = this.onSyncClicked.bind(this);
    this.toggleQrVisibility = this.toggleQrVisibility.bind(this);
  }

  async generateQrCode() {
    // Replace with logo version
    const keyQrCanvas = await qrService.generateQrCodeWithLogo(this.keyCanvas, this.props.encryption_key, logo);
    this.keyCanvas = keyQrCanvas;
  }
  
  toggleQrVisibility() {
    this.setState({ showQr: !this.state.showQr }, async () => {
      if (this.state.showQr && this.keyCanvas) {
        await this.generateQrCode();
      }
    });
  }

  async onDeleteClicked(e) {
    e.preventDefault();
    this.setState({ showDeleteModal: true });
  }

  async onConfirmDelete() {
    this.setState({ deleting: true });
    try {
      await keyService.delete(this.props.id, this.props.isLocal);
      this.props.onKeyDeleted(this.props.id);
      this.setState({ 
        showDeleteModal: false, 
        deleting: false 
      });
    } catch (error) {
      this.setState({ 
        sendingKeyError: 'Unexpected error', 
        deleting: false,
        showDeleteModal: false 
      });
    }
  }

  onCancelDelete() {
    this.setState({ 
      showDeleteModal: false,
      deleting: false 
    });
  }

  async onSyncClicked(e) {
    e.preventDefault();
    this.setState({ syncing: true });
    try {
      const syncedKey = await keyService.syncLocalKeyToSupabase(this.props);
      this.props.onKeySynced(this.props.id, syncedKey);
    } catch (error) {
      console.error('Failed to sync key:', error);
      this.setState({ syncing: false });
      // You could show an error message here
    }
  }

  render() {
    const { id, name, encryption_key, created_at, isLocal, source, t } = this.props;
    const formattedDate = moment(created_at).format('MMM D, YYYY');
    const deleteModalId = `deleteKeyModal-${id}`;

    return (
      <div className={`key-item ${isLocal ? 'local-key' : 'remote-key'}`}>
        <div className="key-item-header">
          <div className="key-title-section">
            <h4 className="key-name">
              <i className="fa fa-key" aria-hidden="true"></i> {name || t('listCodes.untitledKey')}
            </h4>
            {isLocal && (
              <span className="badge badge-warning local-badge">
                <i className="fa fa-laptop" aria-hidden="true"></i> {t('listCodes.localBadge')}
              </span>
            )}
            {!isLocal && (
              <span className="badge badge-success cloud-badge">
                <i className="fa fa-cloud" aria-hidden="true"></i> {t('listCodes.cloudBadge')}
              </span>
            )}
          </div>
          <div className="key-actions">
            <button
              className="btn btn-sm btn-outline-primary"
              onClick={this.toggleQrVisibility}
            >
              <i className={`fa ${this.state.showQr ? 'fa-eye-slash' : 'fa-eye'}`} aria-hidden="true"></i> {this.state.showQr ? t('listCodes.hideKey') : t('listCodes.showKey')}
            </button>
            {isLocal && this.props.isLoggedIn && (
              <button
                className="btn btn-sm btn-outline-success ml-2"
                onClick={this.onSyncClicked}
                disabled={this.state.syncing}
              >
                {this.state.syncing ? (
                  <><i className="fa fa-spinner fa-spin" aria-hidden="true"></i> {t('listCodes.syncing')}</>
                ) : (
                  <><i className="fa fa-cloud-upload-alt" aria-hidden="true"></i> {t('listCodes.saveToCloud')}</>
                )}
              </button>
            )}
            <button
              className="btn btn-sm btn-outline-danger ml-2"
              onClick={this.onDeleteClicked}
              disabled={this.state.deleting}
            >
              {this.state.deleting ? (
                <><i className="fa fa-spinner fa-spin" aria-hidden="true"></i> {t('listCodes.deleting')}</>
              ) : (
                <><i className="fa fa-trash" aria-hidden="true"></i> {t('listCodes.delete')}</>
              )}
            </button>
          </div>
        </div>

        <div className="key-item-details">
          <div className="key-info">
            <p className="key-date">
              <i className="fa fa-calendar" aria-hidden="true"></i> {t('listCodes.created', { date: formattedDate })}
            </p>
            {isLocal && (
              <p className="key-location text-muted">
                <i className="fa fa-laptop" aria-hidden="true"></i> {t('listCodes.storedLocally')}
              </p>
            )}
            {!isLocal && (
              <p className="key-location text-muted">
                <i className="fa fa-cloud" aria-hidden="true"></i> {t('listCodes.storedInAccount')}
              </p>
            )}
            {!this.state.showQr && (
              <p className="key-id text-muted">ID: {id}</p>
            )}
          </div>
          
          {this.state.showQr && (
            <div className="key-qr-container">
              <div className="key-qr">
                <canvas ref={(c) => { this.keyCanvas = c; }} />
              </div>
            </div>
          )}
        </div>

        <DeleteKeyModal
          modalId={deleteModalId}
          keyName={name}
          onConfirm={this.onConfirmDelete}
          onCancel={this.onCancelDelete}
          isDeleting={this.state.deleting}
          show={this.state.showDeleteModal}
        />
      </div>
    );
  }
}

Row.propTypes = {
  id: PropTypes.string.isRequired,
  name: PropTypes.string,
  encryption_key: PropTypes.string.isRequired,
  created_at: PropTypes.string.isRequired,
  isLocal: PropTypes.bool,
  source: PropTypes.string,
  isLoggedIn: PropTypes.bool,
  onKeyDeleted: PropTypes.func.isRequired,
  onKeySynced: PropTypes.func.isRequired,
  t: PropTypes.func,
};

class ListCodes extends Component {
  constructor() {
    super();
    this.state = {
      keys: null,
      isLoading: true,
      error: null,
      syncingAll: false,
    };
    this.fetchKeys = this.fetchKeys.bind(this);
    this.handleKeyDeleted = this.handleKeyDeleted.bind(this);
    this.handleKeySynced = this.handleKeySynced.bind(this);
    this.handleSyncAllLocal = this.handleSyncAllLocal.bind(this);
    this.handleExportKeys = this.handleExportKeys.bind(this);
    this.handleImportKeys = this.handleImportKeys.bind(this);
  }

  // Download all keys as a JSON backup file - the mitigation against
  // localStorage loss (cleared site data, another browser, lost account access)
  handleExportKeys() {
    const { keys } = this.state;
    if (!keys || keys.length === 0) return;

    const backup = {
      format: 'securepaper-keys',
      version: 1,
      exported_at: new Date().toISOString(),
      keys: keys.map(key => ({
        name: key.name || null,
        encryption_key: key.encryption_key,
        created_at: key.created_at || null,
      })),
    };

    const blob = new Blob([JSON.stringify(backup, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = `securepaper-keys-backup-${moment().format('YYYY-MM-DD')}.json`;
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    URL.revokeObjectURL(url);
  }

  async handleImportKeys(event) {
    const file = event.target.files && event.target.files[0];
    event.target.value = ''; // allow re-importing the same file
    if (!file) return;

    const { t } = this.props;
    try {
      const text = await file.text();
      const backup = JSON.parse(text);
      if (backup.format !== 'securepaper-keys' || !Array.isArray(backup.keys)) {
        this.setState({ error: t('listCodes.notBackupFile') });
        return;
      }

      const existing = new Set((this.state.keys || []).map(key => key.encryption_key));
      let imported = 0;
      for (const entry of backup.keys) {
        if (!entry || typeof entry.encryption_key !== 'string' || existing.has(entry.encryption_key)) {
          continue;
        }
        await keyService.createKey({
          encryptionKey: entry.encryption_key,
          name: entry.name || t('listCodes.importedKeyName', { date: moment().format('MMM D, YYYY') }),
          subject: null,
        });
        existing.add(entry.encryption_key);
        imported += 1;
      }

      await this.fetchKeys();
      this.setState({ error: null, importInfo: imported === 0 ? t('listCodes.importedNone') : t('listCodes.importedResult', { count: imported }) });
      setTimeout(() => this.setState({ importInfo: null }), 4000);
    } catch (err) {
      console.error('Key import failed:', err);
      this.setState({ error: t('listCodes.readBackupFailed') });
    }
  }

  componentDidMount() {
    this.fetchKeys();
  }

  componentDidUpdate(prevProps) {
    // Refetch keys when authentication state changes
    if (prevProps.isLoggedIn !== this.props.isLoggedIn) {
      console.log('ListCodes: Auth state changed, refetching keys...');
      this.setState({ isLoading: true, error: null }, () => {
        this.fetchKeys();
      });
    }
  }

  async fetchKeys() {
    try {
      console.log('ListCodes: Starting to fetch keys...');
      // Add timeout to prevent infinite loading
      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => reject(new Error('Request timed out after 10 seconds')), 10000);
      });
      
      const keysPromise = keyService.get();
      
      const keys = await Promise.race([keysPromise, timeoutPromise]);
      
      console.log('ListCodes: Received keys:', keys);
      this.setState({ keys, isLoading: false });
    } catch (error) {
      console.error('ListCodes: Error fetching keys:', error);
      this.setState({ 
        error: error.message || 'Failed to load encryption keys', 
        isLoading: false 
      });
    }
  }

  handleKeyDeleted(deletedKeyId) {
    this.setState({
      keys: this.state.keys.filter(key => key.id !== deletedKeyId)
    });
  }

  handleKeySynced(localKeyId, syncedKey) {
    this.setState({
      keys: this.state.keys.map(key => 
        key.id === localKeyId 
          ? { ...syncedKey, isLocal: false, source: 'remote' }
          : key
      )
    });
  }

  async handleSyncAllLocal() {
    this.setState({ syncingAll: true });
    try {
      const syncResults = await keyService.syncAllLocalKeysToSupabase();
      
      // Refresh the keys list to show updated state
      await this.fetchKeys();
      
      // Show success notification
      if (syncResults.syncedKeys.length > 0) {
        // You could show a toast notification here
        console.log(`Successfully synced ${syncResults.syncedKeys.length} keys to cloud`);
      }
    } catch (error) {
      console.error('Failed to sync all keys:', error);
    } finally {
      this.setState({ syncingAll: false });
    }
  }

  render() {
    const { t } = this.props;
    const { keys, isLoading, error, syncingAll } = this.state;

    if (isLoading) {
      return (
        <div className="loading-container text-center p-4">
          <i className="fa fa-spinner fa-spin fa-2x" aria-hidden="true"></i>
          <p className="loading-text mt-2">{t('listCodes.loading')}</p>
        </div>
      );
    }

    if (error) {
      return (
        <div className="alert alert-danger error-container" role="alert">
          <i className="fa fa-exclamation-triangle" aria-hidden="true"></i>
          <span className="error-message">{t('listCodes.errorLoading', { error })}</span>
        </div>
      );
    }

    if (!keys || keys.length === 0) {
      return (
        <div className="empty-state-container">
          <div className="text-center p-4">
            <i className="fa fa-key fa-3x text-muted mb-3" aria-hidden="true"></i>
            <h4 className="empty-state-heading">{t('listCodes.noKeysFound')}</h4>
            <p className="empty-state-message text-muted">{t('listCodes.noKeysMessage')}</p>
            <Link to="/create_encrypted_text" className="btn btn-primary create-button mt-3">
              <i className="fa fa-plus" aria-hidden="true"></i> {t('listCodes.createNewMessage')}
            </Link>
            <div className="mt-3">
              <label className="btn btn-outline-secondary btn-sm">
                <i className="fa fa-upload" aria-hidden="true"></i> {t('listCodes.importFromBackup')}
                <input type="file" accept="application/json,.json" style={{ display: 'none' }} onChange={this.handleImportKeys} />
              </label>
            </div>
          </div>
        </div>
      );
    }

    const localKeys = keys.filter(key => key.isLocal);
    const remoteKeys = keys.filter(key => !key.isLocal);
    
    return (
      <div className="keys-list">
        <div className="keys-header">
          <h3 className="keys-list-title">
            <i className="fa fa-key" aria-hidden="true"></i> {t('listCodes.yourSavedKeys')}
            <span className="key-count">{keys.length}</span>
          </h3>
          <div className="keys-header-actions">
            {localKeys.length > 0 && (
              <button
                onClick={this.handleSyncAllLocal}
                className="btn btn-outline-success mr-2"
                disabled={syncingAll}
              >
                {syncingAll ? (
                  <><i className="fa fa-spinner fa-spin" aria-hidden="true"></i> {t('listCodes.syncing')}</>
                ) : (
                  <><i className="fa fa-cloud-upload-alt" aria-hidden="true"></i> {t('listCodes.syncAllLocal')}</>
                )}
              </button>
            )}
            <button onClick={this.handleExportKeys} className="btn btn-outline-secondary mr-2" title={t('listCodes.exportTitle')}>
              <i className="fa fa-download" aria-hidden="true"></i> {t('listCodes.export')}
            </button>
            <label className="btn btn-outline-secondary mr-2 mb-0" title={t('listCodes.importTitle')}>
              <i className="fa fa-upload" aria-hidden="true"></i> {t('listCodes.import')}
              <input type="file" accept="application/json,.json" style={{ display: 'none' }} onChange={this.handleImportKeys} />
            </label>
            <Link to="/create_encrypted_text" className="btn btn-primary add-key-button">
              <i className="fa fa-plus" aria-hidden="true"></i> {t('listCodes.createNewKey')}
            </Link>
          </div>
        </div>

        {this.state.importInfo && (
          <div className="alert alert-success py-2">
            <i className="fa fa-check-circle" aria-hidden="true"></i> {this.state.importInfo}
          </div>
        )}

        <div className="alert alert-light py-2 border">
          <i className="fa fa-life-ring text-muted" aria-hidden="true"></i>{' '}
          <small className="text-muted">
            {t('listCodes.backupHint1')} <button type="button" className="btn btn-link btn-sm p-0 align-baseline" onClick={this.handleExportKeys}>{t('listCodes.backupHintLink')}</button> {t('listCodes.backupHint2')}
          </small>
        </div>

        {localKeys.length > 0 && (
          <div className="keys-section">
            <div className="section-header">
              <h4 className="section-title">
                <i className="fa fa-laptop" aria-hidden="true"></i> {t('listCodes.localKeysSection', { count: localKeys.length })}
              </h4>
              <small className="text-muted">{t('listCodes.localKeysDesc')}</small>
            </div>
            <div className="keys-list-container">
              {localKeys.map(key => (
                <Row
                  key={key.id}
                  id={key.id}
                  name={key.name}
                  encryption_key={key.encryption_key}
                  created_at={key.created_at}
                  isLocal={key.isLocal}
                  source={key.source}
                  isLoggedIn={this.props.isLoggedIn}
                  onKeyDeleted={this.handleKeyDeleted}
                  onKeySynced={this.handleKeySynced}
                  t={this.props.t}
                />
              ))}
            </div>
          </div>
        )}

        {remoteKeys.length > 0 && (
          <div className="keys-section mt-4">
            <div className="section-header">
              <h4 className="section-title">
                <i className="fa fa-cloud" aria-hidden="true"></i> {t('listCodes.cloudKeysSection', { count: remoteKeys.length })}
              </h4>
              <small className="text-muted">{t('listCodes.cloudKeysDesc')}</small>
            </div>
            <div className="keys-list-container">
              {remoteKeys.map(key => (
                <Row
                  key={key.id}
                  id={key.id}
                  name={key.name}
                  encryption_key={key.encryption_key}
                  created_at={key.created_at}
                  isLocal={key.isLocal}
                  source={key.source}
                  isLoggedIn={this.props.isLoggedIn}
                  onKeyDeleted={this.handleKeyDeleted}
                  onKeySynced={this.handleKeySynced}
                  t={this.props.t}
                />
              ))}
            </div>
          </div>
        )}
      </div>
    );
  }
}

// Wrapper to inject auth context and the translation function into the class component
const ListCodesWithAuth = () => {
  const { state } = useAuth();
  const { t } = useTranslation();
  return <ListCodes isLoggedIn={state.isLoggedIn} t={t} />;
};

export default ListCodesWithAuth;
